import React, { useEffect, useState } from 'react';
import { Row, Col, Table, Button, Modal, Form, Input, message, Tabs, Tag, Space, Tooltip, Select } from 'antd';
import { EditOutlined, EyeOutlined, PlusOutlined, DeleteOutlined, SettingOutlined } from '@ant-design/icons';
import AxiosOICStat from '../AxiosOICStat';
import { CountryInfo } from './CountryInfo';
import CountryEditModal from '../components/modals/CountryEditModal';
import CountryCreateModal from '../components/modals/CountryCreateModal';
import MissingCountriesModal from '../components/modals/MissingCountriesModal';
import { useSelector } from 'react-redux';
import { Spin } from 'antd';
import RoleUtils from '../utils/RoleUtils';
import { getIndicatorName } from '../utils/indicatorName';

const { TabPane } = Tabs;

const ShowModal = ({ visible, record, onCancel }) => (
    <Modal
        visible={visible}
        title="Country Details"
        onCancel={onCancel}
        footer={[<Button key="cancel" onClick={onCancel}>Close</Button>]}
    >
        {record && <CountryInfo c_code={record.c_code} />}
    </Modal>
);

export const Countries = () => {
    const [countries, setCountries] = useState([]);
    const [originalCountries, setOriginalCountries] = useState([]);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [showModalVisible, setShowModalVisible] = useState(false);
    const [missingCountriesModalVisible, setMissingCountriesModalVisible] = useState(false);
    const [selectedMissingCountries, setSelectedMissingCountries] = useState(null);
    const [selectedMissingCountriesTitle, setSelectedMissingCountriesTitle] = useState('');
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [loading, setLoading] = useState(true);
    const user = useSelector((state) => state.user);
    const indicatorsState = useSelector((state) => state.indicators);
    const getMissingIndicatorName = (record) => getIndicatorName(indicatorsState.value[record.ind_code]) || record.ind_name_eng || '';
    const [searchValue, setSearchValue] = useState('');
    const [missingDataSearchValue, setMissingDataSearchValue] = useState('')
    const [missingCountryData, setMissingCountryData] = useState([]);
    const [originalMissingCountryData, setOriginalMissingCountryData] = useState([]);
    const [exportLoading, setExportLoading] = useState(false);
    const [exportAltNamesLoading, setExportAltNamesLoading] = useState(false);
    const [displayedRegions, setDisplayedRegions] = useState(['OIC', 'Developed Countries', 'Non-OIC Developing Countries']);
    const [regionSettingsVisible, setRegionSettingsVisible] = useState(false);
    const [availableRegions, setAvailableRegions] = useState([]);
    const { Search } = Input;

    useEffect(() => {
        setLoading(true);
        AxiosOICStat.get(`countries`)
            .then((response) => {
                setCountries(response.data);
                setOriginalCountries(response.data);
                
                const allRegions = new Set();
                response.data.forEach(country => {
                    if (country.region_names && Array.isArray(country.region_names)) {
                        country.region_names.forEach(region => allRegions.add(region));
                    }
                });
                setAvailableRegions(Array.from(allRegions).sort());
            })
            .catch(err => console.log(err))
            .finally(() => setLoading(false));

            AxiosOICStat.get('/data/find-missing-countries-for-indicators-data')
            .then((response) => {
                setMissingCountryData(response.data);
                setOriginalMissingCountryData(response.data);
            })
            .catch(err => console.log(err));
    }, []);

    const getRegionColor = (region) => {
        const colorMap = {
            'OIC': 'green',
            'Developed Countries': 'blue',
            'Non-OIC Developing Countries': 'orange'
        };
        return colorMap[region] || 'default';
    };

    const columns = [
        { title: 'Code', dataIndex: 'c_code' },
        {
            title: 'Region',
            dataIndex: 'region_names',
            key: 'region',
            render: (regionNames) => {
                if (!regionNames || !Array.isArray(regionNames)) {
                    return <span>-</span>;
                }
                
                const matchedRegion = displayedRegions.find(region => regionNames.includes(region));
                
                if (matchedRegion) {
                    return <Tag color={getRegionColor(matchedRegion)}>{matchedRegion}</Tag>;
                }
                
                return <span>-</span>;
            },
            filters: displayedRegions.map(region => ({ text: region, value: region })),
            onFilter: (value, record) => {
                if (!record.region_names || !Array.isArray(record.region_names)) {
                    return false;
                }
                return record.region_names.includes(value);
            }
        },
        { title: 'Name', dataIndex: 'c_short_name_eng' },
        { 
            title: 'Alternative Names', 
            dataIndex: 'c_alt_names',
            render: (text) => {
                if (!text) {
                    return <span style={{ color: '#999' }}>No alternatives</span>;
                }
                
                let altNames = [];
                if (Array.isArray(text)) {
                    altNames = text.filter(name => name && name.length > 0);
                } else if (typeof text === 'string') {
                    altNames = text.split(',').map(name => name.trim()).filter(name => name.length > 0);
                }
                
                if (altNames.length === 0) {
                    return <span style={{ color: '#999' }}>No alternatives</span>;
                }
                
                return (
                    <Space wrap size={[0, 8]} style={{ gap: '6px' }}>
                        {altNames.slice(0, 5).map((name, index) => (
                            <Tag key={index} color="blue" style={{ padding: '4px 8px', fontSize: '12px' }}>
                                {name}
                            </Tag>
                        ))}
                        {altNames.length > 5 && (
                            <Tooltip title={altNames.slice(5).join(', ')}>
                                <Tag color="default" style={{ padding: '4px 8px', fontSize: '12px', cursor: 'pointer' }}>
                                    +{altNames.length - 5} more
                                </Tag>
                            </Tooltip>
                        )}
                    </Space>
                );
            },
        },
        {
            title: 'Actions',
            dataIndex: '',
            key: 'actions',
            render: (text, record) => (
                <span style={{ display: "flex" }}>
                    {RoleUtils.canEditRecords(user.role) && <Button type="primary" icon={<EditOutlined />} onClick={() => handleEdit(record)} />}
                    <Button style={{ marginLeft: 8 }} icon={<EyeOutlined />} onClick={() => handleShow(record)} />
                    {RoleUtils.canEditRecords(user.role) && (
                        <Button
                            danger
                            style={{ marginLeft: 8 }}
                            icon={<DeleteOutlined />}
                            onClick={() => handleDelete(record)}
                        />
                    )}
                </span>
            ),
        },
    ];

    const missingCountryDataColumns = [
        { title: 'Indicator Code', dataIndex: 'ind_code'},
        { title: 'Indicator Name', dataIndex: 'ind_name_eng', render: (name, record) => getMissingIndicatorName(record) },
        { 
            title: 'Missing Countries', 
            dataIndex: 'missing_countries',
            render: (text, record) => {
                if (!text) {
                    return <Tag color="green">Complete</Tag>;
                }

                let countries = [];
                if (Array.isArray(text)) {
                    countries = text.filter(c => c && c.length > 0);
                } else if (typeof text === 'string') {
                    countries = text.split(',').map(c => c.trim()).filter(c => c.length > 0);
                }

                if (countries.length === 0) {
                    return <Tag color="green">Complete</Tag>;
                }

                return (
                    <Button 
                        type="primary" 
                        onClick={() => {
                            setSelectedMissingCountries(text);
                            setSelectedMissingCountriesTitle(`Missing Countries - ${getMissingIndicatorName(record)}`);
                            setMissingCountriesModalVisible(true);
                        }}
                    >
                        View ({countries.length})
                    </Button>
                );
            },
        }
    ]

    const handleEdit = (record) => {
        setSelectedRecord(record);
        setEditModalVisible(true);
    };

    const handleDelete = (record) => {
        const id = record.id || record.country_id || record.c_id || record.c_code;
        if (!id) {
            message.error('Unable to determine country id for deletion');
            return;
        }

        Modal.confirm({
            title: `Delete country ${record.c_short_name_eng || record.c_code || id}?`,
            content: 'This action cannot be undone. Are you sure you want to delete this country?',
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await AxiosOICStat.delete(`/countries/delete-country/${id}`);
                    message.success('Country deleted successfully');
                    setCountries(prev => prev.filter(c => (c.id || c.country_id || c.c_id || c.c_code) !== id));
                    setOriginalCountries(prev => prev.filter(c => (c.id || c.country_id || c.c_id || c.c_code) !== id));
                } catch (err) {
                    console.error('Error deleting country:', err);
                    message.error('Failed to delete country');
                }
            }
        });
    };

    const handleShow = (record) => {
        setSelectedRecord(record);
        setShowModalVisible(true);
    };

    const handleEditModalCancel = () => {
        setEditModalVisible(false);
    };

    const handleCreateModalCancel = () => {
        setCreateModalVisible(false);
    };

    const handleCreate = (newCountry) => {
        setCountries(prevCountries => [...prevCountries, newCountry]);
        setOriginalCountries(prevCountries => [...prevCountries, newCountry]);
    };

    const handleShowModalCancel = () => {
        setShowModalVisible(false);
    };

    const handleMissingCountriesModalCancel = () => {
        setMissingCountriesModalVisible(false);
    };

    const handleUpdate = (updatedRecord) => {
        setCountries(prevCountries =>
            prevCountries.map(country =>
                country.c_code === updatedRecord.c_code ? updatedRecord : country
            )
        );
        setOriginalCountries(prevCountries =>
            prevCountries.map(country =>
                country.c_code === updatedRecord.c_code ? updatedRecord : country
            )
        );
    };

    const handleSearch = (value) => {
        setSearchValue(value);
        const lowerCaseValue = value.toLowerCase();
        const filtered = originalCountries.filter(country => {
            const countryCode = country.c_code || '';
            const countryName = country.c_short_name_eng || '';
            return countryName.toLowerCase().includes(lowerCaseValue) || countryCode.toString().includes(lowerCaseValue);
        });
        setCountries(filtered);
    };

    const handleSearchMissingData = (value) => {
        setMissingDataSearchValue(value);
        const lowerCaseValue = value.toLowerCase();
    
        const filtered = originalMissingCountryData.filter(indicator => { 
            const indNameEng = getMissingIndicatorName(indicator);
            return indNameEng.toLowerCase().includes(lowerCaseValue);
        });
    
        setMissingCountryData(filtered);
    };

    const handleExportAllCountries = () => {
        setExportLoading(true);
        AxiosOICStat.get('/countries/export-all-countries', {
            responseType: 'blob',
        })
        .then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'countries_export.csv');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            setExportLoading(false);
        })
        .catch((error) => {
            message.error('Error exporting countries data.');
            console.error('Export error:', error);
            setExportLoading(false);
        });
    };

    const handleExportAlternativeNames = () => {
        setExportAltNamesLoading(true);
        try {
            const exportData = [];
            
            countries.forEach(country => {
                const countryName = country.c_short_name_eng || '';
                const regionName = displayedRegions.find(region => 
                    country.region_names && country.region_names.includes(region)
                ) || '';
                
                const altNames = [];
                if (Array.isArray(country.c_alt_names)) {
                    altNames.push(...country.c_alt_names.filter(name => name && name.length > 0));
                } else if (typeof country.c_alt_names === 'string') {
                    altNames.push(...country.c_alt_names.split(',').map(name => name.trim()).filter(name => name.length > 0));
                }
                
                if (altNames.length === 0) {
                    exportData.push({
                        'OIC Country Name': countryName,
                        'Alternative Name': '',
                        'Region': regionName
                    });
                } else {
                    altNames.forEach(altName => {
                        exportData.push({
                            'OIC Country Name': countryName,
                            'Alternative Name': altName,
                            'Region': regionName
                        });
                    });
                }
            });
            
            const csvContent = [
                ['OIC Country Name', 'Alternative Name', 'Region'].join(','),
                ...exportData.map(row => [
                    `"${(row['OIC Country Name'] || '').replace(/"/g, '""')}"`,
                    `"${(row['Alternative Name'] || '').replace(/"/g, '""')}"`,
                    `"${(row['Region'] || '').replace(/"/g, '""')}"`
                ].join(','))
            ].join('\n');
            
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'alternative_names_export.csv');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            
            message.success('Alternative names exported successfully');
        } catch (error) {
            message.error('Error exporting alternative names.');
            console.error('Export error:', error);
        } finally {
            setExportAltNamesLoading(false);
        }
    };
    
    

    return (
        <Tabs defaultActiveKey="1">
            <TabPane tab="Countries List" key="1">
                <Row>
                    <Search
                        placeholder="Search"
                        allowClear
                        value={searchValue}
                        onChange={(e) => handleSearch(e.target.value)}
                        style={{ marginBottom: 10, width: '100%' }}
                    />
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', width: '100%' }}>
                        {RoleUtils.canEditRecords(user.role) && (
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setCreateModalVisible(true)}
                            >
                                Create Country
                            </Button>
                        )}
                        <Button
                            type="default"
                            onClick={handleExportAllCountries}
                            loading={exportLoading}
                            disabled={exportLoading}
                        >
                            {exportLoading ? 'Exporting...' : 'Export All Countries'}
                        </Button>
                        <Button
                            type="default"
                            onClick={handleExportAlternativeNames}
                            loading={exportAltNamesLoading}
                            disabled={exportAltNamesLoading}
                        >
                            {exportAltNamesLoading ? 'Exporting...' : 'Export Alternative Names'}
                        </Button>
                        <Button
                            icon={<SettingOutlined />}
                            onClick={() => setRegionSettingsVisible(true)}
                        >
                            Configure Regions
                        </Button>
                    </div>
                    <Col xs={24}>
                        <Spin spinning={loading}>
                            <Table
                                rowKey="c_code"
                                dataSource={countries}
                                columns={columns}
                                size="small"
                                pagination={{ pageSize: 10 }}
                                loading={loading}
                            />
                        </Spin>
                    </Col>
                    <CountryEditModal
                        visible={editModalVisible}
                        onCancel={handleEditModalCancel}
                        onUpdate={handleUpdate}
                        record={selectedRecord}
                    />
                    <CountryCreateModal
                        visible={createModalVisible}
                        onCancel={handleCreateModalCancel}
                        onCreate={handleCreate}
                    />
                    <ShowModal
                        visible={showModalVisible}
                        onCancel={handleShowModalCancel}
                        record={selectedRecord}
                    />
                    <MissingCountriesModal
                        visible={missingCountriesModalVisible}
                        onCancel={handleMissingCountriesModalCancel}
                        countries={selectedMissingCountries}
                        title={selectedMissingCountriesTitle}
                    />
                    <Modal
                        title="Configure Region Display"
                        visible={regionSettingsVisible}
                        onCancel={() => setRegionSettingsVisible(false)}
                        onOk={() => setRegionSettingsVisible(false)}
                        width={600}
                    >
                        <p>Select which regions should be displayed in the Region column:</p>
                        <Select
                            mode="multiple"
                            style={{ width: '100%' }}
                            placeholder="Select regions to display"
                            value={displayedRegions}
                            onChange={setDisplayedRegions}
                            options={availableRegions.map(region => ({ label: region, value: region }))}
                        />
                        <p style={{ marginTop: 16, color: '#666', fontSize: '12px' }}>
                            Default regions: OIC, Developed Countries, Non-OIC Developing Countries
                        </p>
                    </Modal>
                </Row>
            </TabPane>

            <TabPane tab="Missing Country Data" key="2">
                <Row>
                    <Search
                        placeholder="Search Missing Data"
                        allowClear
                        value={missingDataSearchValue}
                        onChange={(e) => handleSearchMissingData(e.target.value)}
                        style={{ marginBottom: 10 }}
                    />
                    <Col xs={24}>
                        <Table
                            rowKey="ind_code"
                            dataSource={missingCountryData}
                            columns={missingCountryDataColumns}
                            size="small"
                            pagination={{ pageSize: 15 }}
                            loading={loading}
                        />
                    </Col>
                </Row>
            </TabPane>
        </Tabs>
    );
};